import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Send, MessageSquare, Clock, ChevronDown, ChevronRight, Archive, ArchiveRestore, Pencil } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Support() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [archivedTickets, setArchivedTickets] = useState([]);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [responseText, setResponseText] = useState('');
  const [respondingTo, setRespondingTo] = useState(null);
  const [editingResponse, setEditingResponse] = useState(null);
  const [editText, setEditText] = useState('');
  const [expandedTickets, setExpandedTickets] = useState({});
  const [showArchives, setShowArchives] = useState(false);
  const [expandedArchiveMonths, setExpandedArchiveMonths] = useState({});

  const isAdmin = user?.role === 'admin';

  const fetchTickets = async () => {
    try {
      const res = await axios.get(`${API}/api/support/tickets?archived=false`, { withCredentials: true });
      setTickets(res.data);
    } catch (e) { console.error('Error fetching tickets:', e); }
  };

  const fetchArchivedTickets = async () => {
    try {
      const res = await axios.get(`${API}/api/support/tickets?archived=true`, { withCredentials: true });
      setArchivedTickets(res.data);
    } catch (e) { console.error('Error fetching archived tickets:', e); }
  };

  useEffect(() => {
    fetchTickets();
    fetchArchivedTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !message.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/api/support/tickets`, { title, message }, { withCredentials: true });
      toast.success(t('ticket_sent'));
      setTitle('');
      setMessage('');
      fetchTickets();
    } catch (e) {
      toast.error('Erreur');
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async (ticketId) => {
    if (!responseText.trim()) return;
    try {
      await axios.put(`${API}/api/support/tickets/${ticketId}/respond`, { response: responseText }, { withCredentials: true });
      toast.success(t('response_sent'));
      setResponseText('');
      setRespondingTo(null);
      fetchTickets();
      fetchArchivedTickets();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleEditResponse = async (ticketId) => {
    if (!editText.trim()) return;
    try {
      await axios.put(`${API}/api/support/tickets/${ticketId}/respond`, { response: editText }, { withCredentials: true });
      toast.success(t('response_sent'));
      setEditText('');
      setEditingResponse(null);
      fetchTickets();
      fetchArchivedTickets();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleArchive = async (ticketId) => {
    try {
      await axios.put(`${API}/api/support/tickets/${ticketId}/archive`, {}, { withCredentials: true });
      fetchTickets();
      fetchArchivedTickets();
    } catch (e) { toast.error('Erreur'); }
  };

  const handleUnarchive = async (ticketId) => {
    try {
      await axios.put(`${API}/api/support/tickets/${ticketId}/unarchive`, {}, { withCredentials: true });
      fetchTickets();
      fetchArchivedTickets();
    } catch (e) { toast.error('Erreur'); }
  };

  const markAsRead = async (ticketId) => {
    try {
      await axios.put(`${API}/api/support/tickets/${ticketId}/read`, {}, { withCredentials: true });
    } catch (e) { console.error('Error marking ticket as read:', e); }
  };

  const toggleTicketExpand = (id, ticket) => {
    const wasExpanded = expandedTickets[id];
    setExpandedTickets(prev => ({ ...prev, [id]: !prev[id] }));
    // Mark as read when expanding an unread ticket
    if (!wasExpanded && ticket?.is_unread) {
      markAsRead(id);
      // Update local state to remove bold immediately
      setTickets(prev => prev.map(t => t.id === id ? { ...t, is_unread: false } : t));
      setArchivedTickets(prev => prev.map(t => t.id === id ? { ...t, is_unread: false } : t));
    }
  };

  const groupByMonth = (list) => {
    const groups = {};
    for (const t of list) {
      const d = new Date(t.created_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleString('fr-FR', { month: 'long', year: 'numeric' });
      if (!groups[key]) groups[key] = { label, tickets: [] };
      groups[key].tickets.push(t);
    }
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0])).map(([, v]) => v);
  };

  const renderTicket = (ticket, showArchiveBtn = true) => {
    const isExpanded = expandedTickets[ticket.id];
    const isEditing = editingResponse === ticket.id;
    const isBold = ticket.is_unread;

    return (
      <div key={ticket.id} data-testid={`ticket-${ticket.id}`} className={`border rounded-lg overflow-hidden ${isBold ? 'border-blue-300 bg-blue-50/30' : 'border-slate-200'}`}>
        {/* Header row - always visible */}
        <div
          className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors"
          onClick={() => toggleTicketExpand(ticket.id, ticket)}
          data-testid={`ticket-toggle-${ticket.id}`}
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {isExpanded ? <ChevronDown size={16} className="text-slate-400 flex-shrink-0" /> : <ChevronRight size={16} className="text-slate-400 flex-shrink-0" />}
            <span className={`text-sm truncate ${isBold ? 'font-bold text-slate-900' : 'font-medium text-slate-700'}`}>
              {ticket.title || ticket.message.substring(0, 50)}
            </span>
            {isAdmin && <span className="text-xs text-slate-500 flex-shrink-0">({ticket.commercial_name})</span>}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
            <Badge className={ticket.status === 'open' ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-emerald-100 text-emerald-800 border-emerald-200'}>
              {ticket.status === 'open' ? t('open') : t('closed')}
            </Badge>
            <span className="text-xs text-slate-500 hidden sm:inline">
              <Clock size={12} className="inline mr-1" />
              {new Date(ticket.created_at).toLocaleDateString('fr-FR')}
            </span>
          </div>
        </div>

        {/* Expanded content */}
        {isExpanded && (
          <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
            <p className="text-sm text-slate-800 bg-slate-50 rounded p-3">{ticket.message}</p>

            {/* Admin response */}
            {ticket.response && !isEditing && (
              <div className="bg-emerald-50 rounded p-3 border border-emerald-100 ml-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium text-emerald-700 mb-1">
                    {t('admin_response')} - {ticket.responded_at ? new Date(ticket.responded_at).toLocaleString('fr-FR') : ''}
                  </p>
                  {isAdmin && (
                    <Button
                      data-testid={`edit-response-${ticket.id}`}
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-emerald-600 hover:text-emerald-800"
                      onClick={(e) => { e.stopPropagation(); setEditingResponse(ticket.id); setEditText(ticket.response); }}
                    >
                      <Pencil size={12} className="mr-1" /> {t('edit_response')}
                    </Button>
                  )}
                </div>
                <p className="text-sm text-emerald-900">{ticket.response}</p>
              </div>
            )}

            {/* Edit response form (admin) */}
            {isAdmin && isEditing && (
              <div className="ml-4 space-y-2">
                <Textarea
                  data-testid={`edit-response-input-${ticket.id}`}
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  rows={2}
                  className="border-slate-200"
                />
                <div className="flex gap-2">
                  <Button data-testid={`save-edit-${ticket.id}`} size="sm" onClick={() => handleEditResponse(ticket.id)} className="bg-slate-900 hover:bg-slate-800">
                    {t('save')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setEditingResponse(null); setEditText(''); }}>
                    {t('cancel')}
                  </Button>
                </div>
              </div>
            )}

            {/* Respond form (admin, no response yet) */}
            {isAdmin && !ticket.response && (
              respondingTo === ticket.id ? (
                <div className="ml-4 space-y-2">
                  <Textarea
                    data-testid={`response-input-${ticket.id}`}
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    placeholder={t('type_response')}
                    rows={2}
                    className="border-slate-200"
                  />
                  <div className="flex gap-2">
                    <Button data-testid={`respond-btn-${ticket.id}`} size="sm" onClick={() => handleRespond(ticket.id)} className="bg-slate-900 hover:bg-slate-800">
                      {t('send')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setRespondingTo(null); setResponseText(''); }}>
                      {t('cancel')}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button data-testid={`open-respond-${ticket.id}`} size="sm" variant="outline" className="ml-4" onClick={() => setRespondingTo(ticket.id)}>
                  {t('respond')}
                </Button>
              )
            )}

            {/* Archive button */}
            {showArchiveBtn && (
              <div className="ml-4">
                <Button data-testid={`archive-btn-${ticket.id}`} size="sm" variant="ghost" className="text-slate-500 hover:text-slate-700" onClick={() => handleArchive(ticket.id)}>
                  <Archive size={14} className="mr-1" /> {t('archive')}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const archivedByMonth = groupByMonth(archivedTickets);

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6">
        <h1 data-testid="support-title" className="text-2xl font-semibold text-slate-900 tracking-tight">
          {t('support')}
        </h1>

        {/* New ticket form (commercial only) */}
        {!isAdmin && (
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-base font-medium">{t('new_request')}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs">{t('ticket_title')}</Label>
                  <Input
                    data-testid="support-title-input"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={t('ticket_title_placeholder')}
                    className="border-slate-200"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{t('your_message')}</Label>
                  <Textarea
                    data-testid="support-message-input"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={t('describe_issue')}
                    rows={4}
                    className="border-slate-200"
                  />
                </div>
                <Button data-testid="support-submit-btn" type="submit" disabled={loading || !title.trim() || !message.trim()} className="bg-slate-900 hover:bg-slate-800">
                  <Send size={14} className="mr-2" />
                  {loading ? '...' : t('send')}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Active tickets */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <MessageSquare size={18} />
              {t('active_tickets')} ({tickets.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {tickets.length === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center">{t('no_tickets')}</p>
            ) : (
              <div className="space-y-2">
                {tickets.map((ticket) => renderTicket(ticket, true))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Archived tickets grouped by month */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <div
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => setShowArchives(!showArchives)}
              data-testid="toggle-archives"
            >
              {showArchives ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <Archive size={18} />
                {t('archived_tickets')} ({archivedTickets.length})
              </CardTitle>
            </div>
          </CardHeader>
          {showArchives && (
            <CardContent>
              {archivedByMonth.length === 0 ? (
                <p className="text-sm text-slate-500 py-4 text-center">{t('no_archived_tickets')}</p>
              ) : (
                <div className="space-y-4">
                  {archivedByMonth.map((group) => (
                    <div key={group.label}>
                      <div
                        className="flex items-center gap-2 cursor-pointer py-2 px-2 hover:bg-slate-50 rounded"
                        onClick={() => setExpandedArchiveMonths(prev => ({ ...prev, [group.label]: !prev[group.label] }))}
                        data-testid={`archive-month-${group.label}`}
                      >
                        {expandedArchiveMonths[group.label] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        <span className="text-sm font-medium text-slate-700 capitalize">{group.label}</span>
                        <Badge variant="outline" className="ml-auto text-xs">{group.tickets.length}</Badge>
                      </div>
                      {expandedArchiveMonths[group.label] && (
                        <div className="space-y-2 ml-4 mt-2">
                          {group.tickets.map((ticket) => (
                            <div key={ticket.id} data-testid={`archived-ticket-${ticket.id}`} className="border border-slate-200 rounded-lg overflow-hidden">
                              <div
                                className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-slate-50"
                                onClick={() => toggleTicketExpand(ticket.id, ticket)}
                              >
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  {expandedTickets[ticket.id] ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
                                  <span className="font-medium text-sm text-slate-700 truncate">{ticket.title || ticket.message.substring(0, 50)}</span>
                                  {isAdmin && <span className="text-xs text-slate-500">({ticket.commercial_name})</span>}
                                </div>
                                <span className="text-xs text-slate-400">{new Date(ticket.created_at).toLocaleDateString('fr-FR')}</span>
                              </div>
                              {expandedTickets[ticket.id] && (
                                <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
                                  <p className="text-sm text-slate-800 bg-slate-50 rounded p-3">{ticket.message}</p>
                                  {ticket.response && (
                                    <div className="bg-emerald-50 rounded p-3 border border-emerald-100 ml-4">
                                      <p className="text-xs font-medium text-emerald-700 mb-1">
                                        {t('admin_response')} - {ticket.responded_at ? new Date(ticket.responded_at).toLocaleString('fr-FR') : ''}
                                      </p>
                                      <p className="text-sm text-emerald-900">{ticket.response}</p>
                                    </div>
                                  )}
                                  <Button data-testid={`unarchive-btn-${ticket.id}`} size="sm" variant="ghost" className="ml-4 text-slate-500 hover:text-slate-700" onClick={() => handleUnarchive(ticket.id)}>
                                    <ArchiveRestore size={14} className="mr-1" /> {t('unarchive')}
                                  </Button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          )}
        </Card>
      </div>
    </Layout>
  );
}
